import React, { useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (isAuthenticated && user) {
    const dest = user.is_admin ? '/admin/dashboard' : (user.role === 'professor' ? '/professor-pathway' : '/student-pathway');
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      if (result.success && result.user) {
        // Login successful - redirect based on user role
  const u = result.user;
  if (u.is_admin) navigate('/admin/dashboard');
  else if (u.role === 'professor') navigate('/professor-pathway');
  else navigate('/student-pathway');
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage:
          'linear-gradient(110deg, rgba(4,40,20,0.94), rgba(4,19,40,0.92)), url(https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1950&q=80)',
      }}
    >
      <div className="absolute top-0 left-0 w-full flex justify-between items-center px-6 py-4 text-white text-sm z-10">
        <button
          onClick={() => navigate('/')}
          className="font-semibold tracking-wide uppercase text-xs hover:text-emerald-200 transition"
        >
          Muslim Study Abroad
        </button>
        <Link to="/" className="text-white/80 hover:text-emerald-200 transition">
          ← Back to Home
        </Link>
      </div>
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-12 lg:pt-32 lg:pb-20 flex flex-col lg:flex-row gap-12">
        <div className="text-left max-w-2xl text-white">
          <p className="text-sm uppercase tracking-[0.5em] text-emerald-200/80">Muslim Study Abroad</p>
          <h1 className="font-custom-thin text-4xl lg:text-6xl leading-tight mt-4 mb-6 drop-shadow-2xl">
            Rejoin your pathway and keep the dream moving.
          </h1>
          <p className="text-base lg:text-lg text-white/85 leading-relaxed max-w-2xl drop-shadow">
            Whether you’re a student checking off Step 1 or a professor guiding the next cohort, log in to access your personalized dashboard and continue your global journey.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
              <p className="text-emerald-200 font-semibold mb-1 tracking-wide">Student access</p>
              <p className="text-white/80">Update your Dreaming questionnaire, verify contact info, and advance to Exploring.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
              <p className="text-emerald-200 font-semibold mb-1 tracking-wide">Professor tools</p>
              <p className="text-white/80">Complete onboarding, update cohorts, and monitor your advising pipeline.</p>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="glass-panel bg-white/95 rounded-3xl shadow-2xl border border-white/40 p-8">
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.4em] text-emerald-600">Secure access</p>
              <h2 className="text-2xl font-semibold text-slate-900">Sign in to continue</h2>
              <p className="text-sm text-slate-500">
                Need an account?{' '}
                <Link to="/register" className="font-semibold text-emerald-600 hover:text-emerald-500">
                  Start your pathway
                </Link>
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="username" className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:border-slate-600 dark:bg-slate-900/70 dark:text-slate-100 dark:placeholder-slate-500"
                  placeholder="your.username"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:border-slate-600 dark:bg-slate-900/70 dark:text-slate-100 dark:placeholder-slate-500"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 focus:outline-none focus:ring-4 focus:ring-emerald-200 disabled:opacity-50 transition"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-t-2 border-white rounded-full animate-spin"></span>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
              <div className="text-center text-sm text-slate-500">
                <Link to="/forgot-password" className="font-semibold text-emerald-600 hover:text-emerald-500">
                  Forgot your password?
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
